import { useRef } from 'react';
import {
  Formik,
  Field,
} from 'formik';
import {
  DateTime,
} from 'luxon';
import i18n from 'i18next';

import {
  Modal,
  Button,
  Select,
  Tooltip,
  DatePickerField,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';
import {
  useEventListener,
} from 'web-hooks';

import * as S from './styled';
import {
  OpenedTooltip,
} from 'web-types';

const selectProps = {
  styles: {
    container: provided => ({
      ...provided,
      width: '100%',
    }),
    menu: provided => ({
      ...provided,
      marginTop: '1px',
    }),
  },
  theme: theme => ({
    ...theme,
    borderRadius: 3,
  }),
};

type Props = {
  isOpen: boolean;
  clients: any[];
  workouts: any[];
  initialValues: {
    client?: string;
    workout?: string;
    originalId?: string;
    start: DateTime;
    end: DateTime;
  },
  openedTooltips: OpenedTooltip
  onSubmit: { (data: any, actions: any): void };
  onCancelTooltip: { (): void }
  onLogActivity: { (startDate: DateTime): void };
  onRequestClose: { (): void };
  onUserSelected: { (userId: number): void };
  onRequestCreateWorkout: { (values: any): void };
};

const NewWorkoutEvent = ({
  isOpen,
  clients,
  workouts,
  initialValues,
  openedTooltips,
  onSubmit,
  onLogActivity,
  onRequestClose,
  onUserSelected,
  onCancelTooltip,
  onRequestCreateWorkout,
}: Props) => {
  const target = useRef<HTMLDivElement>(null);
  useEventListener(
    'click',
    () => {
      onCancelTooltip?.()
    },
    {
      target: target.current,
      disabled: !isOpen,
    },
  );

  return (
    <Modal
      title={initialValues.originalId ? 'Edit' : i18n.t('schedule.new.workout')}
      isOpen={isOpen}
      contentStyle={{ width: 400 }}
      onRequestClose={onRequestClose}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {(formikProps) => (
          <Flex
            as="form"
            ref={target}
            column
            width="100%"
            onSubmit={formikProps.handleSubmit}
          >
            {!initialValues.originalId && (
              <Flex width="100%" justify="flex-end">
                <Button appearance="link" onClick={() => onLogActivity(formikProps.values.start)}>
                  Log Activity instead
                </Button>
              </Flex>
            )}
            <Tooltip
              body={i18n.t('tooltip.calendar.schedule.modal')}
              isOpen={openedTooltips.some(el => el.key === 'schedule_workout_form')}
            >
              <Flex width="100%" column>
                <S.Label htmlFor="client-select">
                  {i18n.t('client')}
                </S.Label>
                <Select
                  {...selectProps}
                  id="client-select"
                  value={clients
                    ? clients.find(option => option.value === String(formikProps.values.client))
                    : ''}
                  onChange={(option: Option) => {
                    formikProps.setFieldValue('client', option.value);
                    formikProps.setFieldValue('workout', null);
                    onUserSelected(option.value);
                  }}
                  onBlur={formikProps.handleBlur}
                  name="client"
                  options={clients}
                />
              </Flex>
            </Tooltip>
            <Flex width="100%" column>
              <S.Label>
                Time
              </S.Label>
              <Flex spacing={10}>
                <Field
                  name="start"
                  dateFormat="MM/dd/yyyy   hh:mm a"
                  showTimeSelect
                  minDate={new Date()}
                  onChange={(date: Date) => {
                    const start = DateTime.fromJSDate(date);
                    formikProps.setFieldValue('start', start);
                    formikProps.setFieldValue('end', start.plus({ hours: 1 }));
                  }}
                  component={DatePickerField}
                />
                <Field
                  name="end"
                  selected={new Date(formikProps.values.start.toISO())}
                  dateFormat="MM/dd/yyyy   hh:mm a"
                  showTimeSelect
                  minDate={new Date(formikProps.values.start.toISO())}
                  onChange={(date: Date) => {
                    formikProps.setFieldValue('end', DateTime.fromJSDate(date));
                  }}
                  component={DatePickerField}
                />
              </Flex>
            </Flex>
            <Flex width="100%" column>
              <S.Label htmlFor="workout-select">
                {i18n.t('Workout')}
              </S.Label>
              <Select
                {...selectProps}
                id="workout-select"
                value={workouts ? workouts.find(option => option.value === formikProps.values.workout) || '' : ''}
                onBlur={formikProps.handleBlur}
                onChange={(option: Option) => formikProps.setFieldValue('workout', option.value)}
                name="workout"
                options={workouts}
              />
            </Flex>
            <Flex
              width="100%"
              align="center"
              justify="space-between"
              margin="15px 0 0 0"
            >
              <Text color="#9ea0a1">
                Or you can
              </Text>
              <Button
                ghost
                appearance="primary"
                onClick={() => onRequestCreateWorkout(formikProps.values)}
                isDisabled={formikProps.isSubmitting}
              >
                {i18n.t('create.new.workout')}
              </Button>
            </Flex>
            <S.Footer>
              <hr />
              <Flex width="100%" justify="space-between">
                <Button
                  ghost
                  appearance="danger"
                  onClick={onRequestClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  appearance="primary"
                  isLoading={formikProps.isSubmitting}
                  isDisabled={
                    formikProps.isSubmitting
                    || !formikProps.values.start
                    || !formikProps.values.end
                    || !formikProps.values.workout
                    || !formikProps.values.client
                  }
                >
                  Schedule
                </Button>
              </Flex>
            </S.Footer>
          </Flex>
        )}
      </Formik>
    </Modal>
  );
}

export default NewWorkoutEvent;
